import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, User, Shield } from 'lucide-react';
import { useAuth, LanguageToggle } from '../App';

const Navbar = () => {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/categories', label: t('nav.categories') },
    { href: '/seminars', label: t('nav.seminars') },
    { href: '/live', label: t('nav.live', { defaultValue: 'Live' }) },
    { href: '/calendar', label: t('nav.calendar') },
    { href: '/pricing', label: t('nav.pricing') },
    ...(user ? [{ href: '/achievements', label: t('nav.achievements') }] : []),
  ];

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled || mobileMenuOpen
          ? 'bg-misel-bg/95 backdrop-blur-md border-b border-white/5'
          : 'nav-gradient'
      }`}
      data-testid="navbar"
    >
      <div className="px-6 md:px-12 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="font-roboto font-bold text-lg md:text-xl text-white tracking-[0.2em] hover:text-primary transition-colors"
            data-testid="logo-link"
          >
            MISEL GROUP DIGITAL LAB
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.href
                    ? 'text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageToggle />
            
            {user ? (
              <div className="flex items-center gap-4">
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-primary/20 text-primary text-sm font-medium hover:bg-primary/30 transition-colors"
                    data-testid="admin-link"
                  >
                    <Shield size={16} />
                    {t('nav.admin')}
                  </Link>
                )}
                <Link
                  to="/profile"
                  className="flex items-center gap-2 px-4 py-2 rounded-sm bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-colors"
                  data-testid="profile-link"
                >
                  <User size={16} />
                  {t('nav.profile')}
                </Link>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="text-gray-300 hover:text-white text-sm font-medium transition-colors"
                  data-testid="login-link"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-medium rounded-sm transition-colors"
                  data-testid="register-link"
                >
                  {t('nav.register')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-white"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pt-6 pb-4 border-t border-white/10 mt-4">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-lg font-medium transition-colors ${
                    location.pathname === link.href
                      ? 'text-white'
                      : 'text-gray-400'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              <div className="pt-4 border-t border-white/10 flex flex-col gap-4">
                <LanguageToggle />
                
                {user ? (
                  <>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="flex items-center gap-2 text-primary font-medium"
                      >
                        <Shield size={18} />
                        {t('nav.admin')}
                      </Link>
                    )}
                    <Link
                      to="/profile"
                      className="flex items-center gap-2 text-white font-medium"
                    >
                      <User size={18} />
                      {t('nav.profile')}
                    </Link>
                  </>
                ) : (
                  <div className="flex flex-col gap-3">
                    <Link
                      to="/login"
                      className="text-gray-300 font-medium"
                    >
                      {t('nav.login')}
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-2 bg-primary text-white text-center font-medium rounded-sm"
                    >
                      {t('nav.register')}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
